import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { useDataProvider, useNotify, useQueryWithStore } from "ra-core";
import React from "react";
import { deliveryActions } from "../util";

interface Props {
  selectedIds?: number[];
  onComplete?: () => void;
}
interface AutoCompleteProps {
  full_name: string;
  id?: number;
}
const AssignMultipleDriverDialog: React.FC<Props> = (props) => {
  const dataProvider = useDataProvider();
  const { selectedIds, onComplete } = props;
  const [open, setOpen] = React.useState(false);
  const [assignableDeliveries, setAssignableDeliveries] = React.useState<any[]>(
    []
  );
  const [isLoading, setIsLoading] = React.useState(false);
  const notify = useNotify();
  const { data: drivers } = useQueryWithStore({
    type: "getList",
    resource: "drivers",
    payload: {
      sort: { field: "full_name", order: "asc" },
      pagination: { page: 1, perPage: 1000 },
      filter: { "user.status": "active" },
    },
  });
  const [selectedDriver, setSelectedDriver] = React.useState<AutoCompleteProps>(
    {
      full_name: "",
    }
  );

  const handleClose = () => {
    setOpen(false);
    setSelectedDriver({ full_name: "" });
  };

  const handleOpenDialog = async () => {
    if (!selectedIds || selectedIds.length === 0) {
      notify("Please select deliveries first", "warning");
      return;
    }

    setAssignableDeliveries(selectedIds.map((id) => ({ id })));
    setOpen(true);
  };

  const handleAccept = async () => {
    if (!selectedDriver.id) {
      notify("Please select a driver", "warning");
      return;
    }

    setIsLoading(true);

    try {
      // Assign each delivery individually
      const updatePromises = assignableDeliveries.map((delivery) => {
        return dataProvider.update("deliveries", {
          id: delivery.id,
          data: {
            action: deliveryActions.ASSIGN,
            driver_id: selectedDriver.id,
          },
          previousData: delivery,
        });
      });

      await Promise.all(updatePromises);

      notify(
        `Successfully assigned a driver to ${assignableDeliveries.length} deliveries.`,
        "info"
      );
      setOpen(false);
      setSelectedDriver({ full_name: "" });
      onComplete?.(); // Refresh and clear selection
    } catch (error: any) {
      notify(`Error: ${error.message}`, "warning");
    } finally {
      setIsLoading(false);
    }
  };

  const hasSelection = selectedIds && selectedIds.length > 0;

  return (
    <>
      <Button
        color="primary"
        onClick={handleOpenDialog}
        size={"small"}
        disabled={!hasSelection}
      >
        Assign Driver {hasSelection ? `(${selectedIds.length})` : ""}
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id="form-dialog-title">Assign Driver</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have <strong>{selectedIds?.length || 0} deliveries</strong>{" "}
            selected.
            <br />
            Please select a driver to assign to these deliveries.
          </DialogContentText>
          <Autocomplete
            disableClearable
            id="select-driver"
            options={drivers}
            getOptionLabel={(driver: { full_name: string }) => driver.full_name}
            renderInput={(params) => <TextField {...params} label="Driver" />}
            onChange={(event, value: any) => setSelectedDriver(value)}
            value={selectedDriver}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleAccept}
            color="primary"
            disabled={!selectedDriver.id || isLoading}
          >
            {isLoading
              ? "Assigning..."
              : `Assign Driver to ${selectedIds?.length || 0} Deliveries`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AssignMultipleDriverDialog;
